import React, { useEffect, useState } from 'react';
import { Alert, Badge, Card, Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import {
    CATEGORY_LABELS,
    STATUS_LABELS,
    STATUS_VARIANTS,
} from '@/shared/constants/listings';
import {
    REQUEST_STATUS_LABELS,
    REQUEST_STATUS_VARIANTS,
} from '@/shared/constants/requests';
import requestService from '@/shared/services/requestService';
import usePageTitle from '@/shared/hooks/usePageTitle';
import './requests.css';

// Active is the pair of statuses something is still owed on: an open request
// waits on the poster, an accepted one waits on the handover.
const GROUPS = [
    { heading: 'Active', statuses: ['open', 'accepted'] },
    { heading: 'Closed', statuses: ['declined', 'withdrawn'] },
];

const GONE = 'This listing is no longer available.';

function RequestCardItem({ request }) {
    // `mine` projects the listing, and the projection comes back empty when
    // the document behind it is gone. A card that assumed it was there would
    // take the whole page down with it.
    const listing = request.listing || null;
    const categoryLabel = listing
        ? CATEGORY_LABELS[listing.category] || listing.category
        : 'Item';
    const photo = request.listingPhotoUrl;
    return (
        <Card className="h-100">
            <Card.Body className="d-flex gap-3">
                {/* `mine` signs a thumbnail only for a listing that
                    survived, so there is no photo without a title. */}
                {listing && photo ? (
                    <img
                        src={photo}
                        alt={listing.title}
                        className="afe-request-thumb"
                    />
                ) : (
                    <div className="afe-request-thumb-placeholder">
                        {categoryLabel}
                    </div>
                )}
                <div>
                    <Card.Title as="h3" className="h6 mb-1">
                        {listing ? (
                            <Link to={`/listings/${listing._id}`}>
                                {listing.title}
                            </Link>
                        ) : (
                            GONE
                        )}
                    </Card.Title>
                    <div className="mb-2">
                        {listing && (
                            <>
                                <InstitutionLogo
                                    institution={listing.institution}
                                    className="me-1"
                                />
                                <Badge bg={STATUS_VARIANTS[listing.status]}>
                                    {STATUS_LABELS[listing.status] ||
                                        listing.status}
                                </Badge>{' '}
                            </>
                        )}
                        <Badge bg={REQUEST_STATUS_VARIANTS[request.status]}>
                            {REQUEST_STATUS_LABELS[request.status] ||
                                request.status}
                        </Badge>
                    </div>
                    {/* Every card carries the same link text, so the item it
                        opens is what tells them apart out of context. */}
                    <Link
                        to={`/requests/${request._id}`}
                        aria-label={`Open thread for ${
                            listing ? listing.title : 'this request'
                        }`}
                    >
                        Open thread
                    </Link>
                </div>
            </Card.Body>
        </Card>
    );
}

function MyRequestsPage() {
    usePageTitle('My requests');
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        async function load() {
            try {
                const data = await requestService.mine();
                setRequests(data.requests || []);
            } catch (_error) {
                setError(
                    'Could not load your requests. Check your connection and' +
                        ' try again.'
                );
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    if (loading) {
        return (
            <Container className="py-4">
                <LoadingSpinner message="Loading your requests" />
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <h1 className="h3 mb-3">My requests</h1>
            {error && <Alert variant="danger">{error}</Alert>}
            {!error && requests.length === 0 && (
                <p className="text-muted">
                    You have not requested anything yet. Browse the listings to
                    find something.
                </p>
            )}
            {requests.length > 0 &&
                GROUPS.map((group) => {
                    const items = requests.filter((request) =>
                        group.statuses.includes(request.status)
                    );
                    return (
                        <section
                            key={group.heading}
                            aria-label={group.heading}
                            className="mb-4"
                        >
                            <h2 className="h5">{group.heading}</h2>
                            {items.length === 0 ? (
                                <p className="text-muted">
                                    Nothing in this group.
                                </p>
                            ) : (
                                <Row xs={1} md={2} className="g-3">
                                    {items.map((request) => (
                                        <Col key={request._id}>
                                            <RequestCardItem
                                                request={request}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </section>
                    );
                })}
        </Container>
    );
}

export default MyRequestsPage;
